import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Space, Table, Input, Tooltip, Col, DatePicker, message, Form, Modal, Row, Checkbox, Select, ConfigProvider } from "antd";
import type { TableColumnsType } from "antd";
import { ExportOutlined, EditOutlined, CarOutlined, RedoOutlined } from "@ant-design/icons";
import "../../style/enquiry.css";
import AssignModel from "../../components/Assignmodel";
import dayjs from "dayjs";
import { getEnquiryByDateRequest, updateEnquiryRequest, getVehicleTypesRequest, getLocationsRequest, getGoodsTypesRequest, getTripTypesRequest, getPackagesRequest, getIntercityListRequest, getNightFareRequest, enquiryAssignTripRequest, enquiryAssignTripReset } from "../../redux/actions/bookingActions";
import { useListAutoRefresh, ENQUIRY_LIST_EVENTS } from "../../utils/hooks/useListAutoRefresh";
import { buildDatePayload } from "../../utils/dateUtils";
import { noRowSelectionBgTheme } from "../../utils/tableTheme";
import { useBranchId } from "../../utils/hooks/useBranch";
import { useMountEffectOnce } from "../../utils/hooks/useMountEffectOnce";
import { COPY_NAME_CELL } from "../../utils/copyGuard";

interface DataType {
  key: string;
  name: string;
  age: number;
  address: string;
  [key: string]: any;
}
const { RangePicker } = DatePicker;
const { Search } = Input;

const ENQUIRY_REFRESH_DEBOUNCE_MS = 400;

const pageWrapper: React.CSSProperties = {
  padding: 0,
  background: "linear-gradient(135deg,#f7f2fb,#fbf7ff)",
  minHeight: "clamp(520px, 78vh, 900px)",
  display: "flex",
  flexDirection: "column",
};

const Enquiry: React.FC = () => {
  const dispatch = useDispatch();
  const branchId = useBranchId();
  const { enquiries, enquiriesLoading, enquiriesCount, enqToBookingSuccess, enqToBookingError, vehicleTypes, locations, goodsTypes, updateEnquiryLoading,
    updateEnquirySuccess,
    updateEnquiryError, tripTypes, packages, intercityList, nightFare,
    enquiryAssignLoading, enquiryAssignSuccess, enquiryAssignError } = useSelector((state: any) => state.booking || {});

  useMountEffectOnce(() => {
    if (!vehicleTypes?.length) dispatch(getVehicleTypesRequest(branchId));
    if (!locations?.length) dispatch(getLocationsRequest(branchId));
    if (!goodsTypes?.length) dispatch(getGoodsTypesRequest());
    if (!tripTypes?.length) dispatch(getTripTypesRequest());
  });

  const [selectedRowKey, setSelectedRowKey] = useState<string | null>(null);
  const tableClass = "Live-table";
  const today = dayjs();
  const [fromDate, setFromDate] = useState(today.subtract(3, "month"));
  const [toDate, setToDate] = useState(today);
  const [editOpen, setEditOpen] = useState(false);
  const [editRecord, setEditRecord] = useState<any>(null);
  const [form] = Form.useForm();
  const [showFareDetails, setShowFareDetails] = useState(false);
  const [selectedFareDetail, setSelectedFareDetail] = useState<any>(null);
  const refreshTimerRef = useRef<number | undefined>(undefined);
  const fetchEnquiriesRef = useRef<() => void>(() => { });
  const lastFetchedRangeRef = useRef<string | null>(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [searchText, setSearchText] = useState<string>("");

  const convertMinutesToHrs = (minutes: number): string => {
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hrs}.${mins === 0 ? "00" : mins} Hrs`;
  };

  const handleFareTypeChange = (value: any) => {
    const vehicleId = form.getFieldValue("vehicletype");
    setShowFareDetails([2, 3, 4].includes(Number(value)));
    form.setFieldsValue({ faredetails: undefined });
    setSelectedFareDetail(null);
    if (!vehicleId || !value) return;
    switch (Number(value)) {
      case 2: dispatch(getPackagesRequest(vehicleId, branchId)); break;
      case 3: dispatch(getIntercityListRequest(vehicleId, branchId)); break;
      case 4: dispatch(getNightFareRequest(vehicleId, branchId)); break;
    }
  };

  const handleEditSave = () => {
    form.validateFields().then((values) => {
      const username = localStorage.getItem("username") || "Admin";
      const fareType = Number(values.triptype);

      const intercityLabel = fareType === 3 && selectedFareDetail
        ? `${selectedFareDetail.basekm}-${selectedFareDetail.basefare} - ${convertMinutesToHrs(selectedFareDetail.baseminute)}`
        : values.faredetails;

      dispatch(updateEnquiryRequest({
        id: editRecord?.id,
        updatedby: username,
        branchid: branchId,
        name: values.name,
        mobileno: values.mobileno,
        fromloc: values.fromloc,
        toloc: values.toloc,
        distance: values.distance,
        goodstype: values.goods,
        remarks: values.remarks,
        reason: values.Reason,
        tripdate: values.bookeddate ? values.bookeddate.format("YYYY-MM-DD") : undefined,
        triptime: values.bookeddate ? values.bookeddate.format("HH:mm:ss") : undefined,
        triptype: values.triptype,
        packagetype: fareType === 2 ? values.faredetails : undefined,
        intercitytype: fareType === 3 ? intercityLabel : undefined,
        nighttype: fareType === 4 ? values.faredetails : undefined,
        vechicletype: values.vehicletype ?? editRecord?.vehicletypeid,
        iscredit: editRecord?.iscredit,
        iscv: editRecord?.iscv,
        isadditionalfare: editRecord?.isadditionalfare,
        istollgate: editRecord?.istollgate,
        isgst: editRecord?.isgst,
        noofvechicles: editRecord?.noofvechicles,
      }));
    });
  };

  const columns: TableColumnsType<DataType> = [
    {
      title: "Enq ID",
      dataIndex: "id",
      key: "id",
      align: "center",
      width: 120,
      fixed: "left",
      render: (text) => <strong style={{ color: "#89339c" }}>{text}</strong>,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      className: COPY_NAME_CELL,
      align: "center",
      width: 200,
      fixed: "left",
    },
    {
      title: "Mobile",
      dataIndex: "mobileno",
      key: "mobileno",
      align: "center",
      width: 120,
      fixed: "left",
    },
    {
      title: "Date",
      dataIndex: "bookedtime",
      key: "Date",
      align: "center",
      width: 120,
      render: (text) => text ? new Date(text).toLocaleDateString() : "-",
    },
    {
      title: "Time",
      key: "time",
      align: "center",
      width: 150,
      render: (_: any, record: any) => (
        <div>
          <div>
            B - {record.bookedtime ? dayjs(record.bookedtime).format("hh:mm A") : "-"}
          </div>
        </div>
      ),
    },
    {
      title: "From Location",
      dataIndex: "fromloc",
      key: "fromloc",
      align: "center",
      width: 150,
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text} placement="topLeft">
          <span>{text || "-"}</span>
        </Tooltip>
      ),
    },
    {
      title: "To Location",
      dataIndex: "toloc",
      key: "toloc",
      align: "center",
      width: 150,
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text} placement="topLeft">
          <span>{text || "-"}</span>
        </Tooltip>
      ),
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      align: "center",
      width: 130,
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      align: "center",
      width: 130,
    },
    {
      title: "Distance",
      dataIndex: "distance",
      key: "distance",
      align: "center",
      width: 120,
    },
    {
      title: "Fare Type",
      dataIndex: "triptype",
      key: "triptype",
      align: "center",
      width: 200,
      render: (_: any, record: any) => {
        const byId = tripTypes?.find((t: any) => String(t.id) === String(record.triptype));
        if (byId) return byId.name;

        const byName = tripTypes?.find((t: any) => t.name === record.triptype);
        if (byName) return byName.name;

        return record.faretype || "-";
      }
    },
    {
      title: "Credit",
      dataIndex: "iscredit",
      key: "iscredit",
      align: "center",
      width: 100,
      render: (val) => val ? "Yes" : "No",
    },
    {
      title: "Goods",
      dataIndex: "goods",
      key: "goods",
      align: "center",
      width: 250,
    },
    {
      title: "Enquiry By",
      dataIndex: "enquiryby",
      key: "enquiryby",
      align: "center",
      width: 130,
      render: (_: any, record: any) => {
        const updatedBy = String(record.eupdatedby ?? "").trim();

        return (
          <div>
            <div>E - {record.enquiryby}</div>
            {updatedBy && <div>U - {updatedBy}</div>}
          </div>
        );
      },
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
      align: "center",
      width: 130,
    },
    {
      title: "Edit",
      key: "action",
      align: "center",
      width: 80,
      render: (_: any, record: any) => (
        <Space size="middle">
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => {
                setEditRecord(record);

                const rawEnquiryBy = record.enquiryby?.replace(/^[UE] - /, "") ?? "";

                const resolvedTriptypeId = (() => {
                  if (record.triptype) {
                    const byId = tripTypes?.find((t: any) => String(t.id) === String(record.triptype));
                    if (byId) return String(byId.id);
                    const byName = tripTypes?.find((t: any) => t.name === record.triptype);
                    if (byName) return String(byName.id);
                  }
                  if (record.faretype) {
                    const byFaretype = tripTypes?.find((t: any) => t.name === record.faretype);
                    if (byFaretype) return String(byFaretype.id);
                  }
                  return undefined;
                })();

                const numTriptype = resolvedTriptypeId ? Number(resolvedTriptypeId) : 0;
                setShowFareDetails([2, 3, 4].includes(numTriptype));

                let faredetails = undefined;
                if (numTriptype === 2) {
                  faredetails = record.packagefaretype;
                  dispatch(getPackagesRequest(record.vehicletypeid, branchId));
                } else if (numTriptype === 3) {
                  faredetails = record.intercitytype;
                  dispatch(getIntercityListRequest(record.vehicletypeid, branchId));
                } else if (numTriptype === 4) {
                  dispatch(getNightFareRequest(record.vehicletypeid, branchId));
                }

                form.setFieldsValue({
                  id: record.id,
                  name: record.name,
                  mobileno: record.mobileno,
                  enquiryby: rawEnquiryBy,
                  city: record.city,
                  distance: record.distance,
                  iscredit: record.iscredit,
                  fromloc: record.fromloc,
                  toloc: record.toloc,
                  goods: record.goods,
                  vehicletype: record.vehicletypeid,
                  triptype: resolvedTriptypeId,
                  faredetails: faredetails,
                  remarks: record.remarks,
                  bookeddate: record.bookedtime ? dayjs(record.bookedtime) : null,
                });

                setEditOpen(true);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const fetchEnquiries = () => {
    if (!fromDate || !toDate) return;
    const { fromDate: start, toDate: end } = buildDatePayload(fromDate, toDate.add(1, "day"));
    dispatch(getEnquiryByDateRequest(branchId, start, end));
  };

  fetchEnquiriesRef.current = fetchEnquiries;
  const scheduleRefresh = () => {
    window.clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = window.setTimeout(
      () => fetchEnquiriesRef.current(),
      ENQUIRY_REFRESH_DEBOUNCE_MS
    );
  };

  useListAutoRefresh(ENQUIRY_LIST_EVENTS, fetchEnquiries);

  useEffect(() => () => window.clearTimeout(refreshTimerRef.current), []);

  useEffect(() => {
    if (enqToBookingSuccess) {
      message.success("Enquiry converted to booking successfully!");
      dispatch({ type: "ENQUIRY_TO_NEW_BOOKING_RESET" });
      setSelectedRowKey(null);
      scheduleRefresh();
    } else if (enqToBookingError) {
      message.error(enqToBookingError);
      dispatch({ type: "ENQUIRY_TO_NEW_BOOKING_RESET" });
    }
  }, [enqToBookingSuccess, enqToBookingError, dispatch, fromDate, toDate]);

  useEffect(() => {
    if (!fromDate || !toDate) return;
    const rangeKey = `${branchId}|${fromDate.valueOf()}|${toDate.valueOf()}`;
    if (lastFetchedRangeRef.current === rangeKey) return;
    lastFetchedRangeRef.current = rangeKey;
    fetchEnquiries();
  }, [dispatch, branchId, fromDate, toDate]);

  useEffect(() => {
    if (updateEnquirySuccess) {
      message.success("Enquiry updated successfully");
      setEditOpen(false);
      form.resetFields();
      dispatch({ type: "UPDATE_ENQUIRY_RESET" });
      scheduleRefresh();
    }
    else if (updateEnquiryError) {
      message.error(updateEnquiryError);
      dispatch({ type: "UPDATE_ENQUIRY_RESET" });
    }
  }, [updateEnquirySuccess, updateEnquiryError, dispatch, fromDate, toDate]);

  useEffect(() => {
    if (enquiryAssignSuccess) {
      message.success("Trip assigned successfully");
      setAssignOpen(false);
      dispatch(enquiryAssignTripReset());
      dispatch({ type: "RESET_SELECTED_CAB" });
      setSelectedRowKey(null);
      scheduleRefresh();
    }
    else if (enquiryAssignError) {
      message.error(enquiryAssignError);
      dispatch(enquiryAssignTripReset());
    }
  }, [enquiryAssignSuccess, enquiryAssignError, dispatch, fromDate, toDate]);

  const handleDateRangeChange = (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      setFromDate(dates[0]);
      setToDate(dates[1]);
    } else {
      const now = dayjs();
      setFromDate(now.subtract(3, "month"));
      setToDate(now);
    }
  };

  const handleRefresh = () => {
    setSearchText("");
    setSelectedRowKey(null);
    fetchEnquiries();
  };

  const handleBookNow = () => {
    if (!selectedRowKey) return;
    const username = localStorage.getItem("username") || "";
    dispatch({
      type: "ENQUIRY_TO_NEW_BOOKING_REQUEST",
      payload: { enquiryid: selectedRowKey, branchid: branchId, username }
    });
  };

  const dataSource = Array.isArray(enquiries)
    ? enquiries.map((item: any, i: number) => ({
      ...item,
      key: item.id != null ? String(item.id) : `row-${i}`,
    }))
    : [];

  const filteredBookings = dataSource.filter((enquiry: DataType) => {
    if (!searchText) return true;
    const lower = searchText.toLowerCase();
    return (
      enquiry.id?.toString().toLowerCase().includes(lower) ||
      enquiry.mobileno?.toString().toLowerCase().includes(lower) ||
      enquiry.name?.toString().toLowerCase().includes(lower)
    );
  });

  const rowSelection = {
    type: "radio" as const,
    selectedRowKeys: selectedRowKey !== null ? [selectedRowKey] : [],
    onChange: (keys: React.Key[]) => {
      const newKey = keys.length > 0 ? keys[0]?.toString() : null;
      setSelectedRowKey(newKey === selectedRowKey ? null : newKey);
    },
  };

  const selectedEnquiry =
    dataSource.find(
      (item: any) => String(item.key) === selectedRowKey
    ) || null;

  return (
    <div style={pageWrapper}>
      <div className="toolbar-glass">
        <Space size={[40, 16]} wrap>
          <RangePicker
            className="custom-range-picker"
            format="DD-MM-YYYY"
            value={fromDate && toDate ? [fromDate, toDate] : null}
            onChange={handleDateRangeChange}
          />

          <Tooltip title="Refresh">
            <Button className="btn-export" onClick={handleRefresh}>
              <RedoOutlined />
            </Button>
          </Tooltip>
        </Space>

        <Space size="large" wrap>
          <div className="active-pill" style={{ fontSize: "18px" }}>
            <CarOutlined />
            Enquiries : {enquiriesCount || dataSource.length}
          </div>

          <Search
            className="custom-search-complete"
            placeholder="Search enquiries..."
            style={{ width: "100%", maxWidth: 250, minWidth: 170 }}
            size="large"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            onClear={() =>
              setSearchText("")
            }
          />

          <Tooltip title="Export">
            <Button className="btn-export">
              <ExportOutlined />
            </Button>
          </Tooltip>
        </Space>
      </div>

      <div style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
        <div className="action-btn-group">
          <AssignModel
            open={assignOpen}
            setOpen={setAssignOpen}
            selectedRowKey={selectedRowKey}
            selectedBookingData={selectedEnquiry}
            assignLoading={enquiryAssignLoading}
            onAssignConfirm={(rowKey, vehicleData) => {
              if (!vehicleData) {
                message.warning("Please select a vehicle");
                return;
              }
              const assignedby = localStorage.getItem("username") || "Admin";
              dispatch(
                enquiryAssignTripRequest({
                  cabid: vehicleData.cabId,
                  driverid: vehicleData.driverid,
                  enquiryid: rowKey,
                  assignedby: assignedby,
                  emptyKM: vehicleData.distance,
                })
              );
            }}
          />
          <Button className="action-btn secondary" disabled={!selectedRowKey} onClick={handleBookNow}>Book Now</Button>
        </div>

        <div
          style={{
            width: "100%",
            flex: 1,
            minHeight: 0,
            minWidth: 0,
          }}
        >
          <ConfigProvider theme={noRowSelectionBgTheme}>
            <Table<DataType>
              bordered
              className={tableClass}
              rowSelection={rowSelection}
              columns={columns}
              dataSource={filteredBookings}
              loading={enquiriesLoading}
              rowKey="key"
              scroll={{
                x: 2600,
                y: "calc(100vh - 340px)",
              }}
              pagination={{
                pageSizeOptions: ["5", "10", "100"],
                showSizeChanger: true,
                defaultPageSize: 10,
              }}
            />
          </ConfigProvider>
        </div>
      </div>

      <Modal
        open={editOpen}
        destroyOnHidden
        footer={null}
        width={{ xs: "94vw", md: 680 }}
        onCancel={() => {
          setEditOpen(false);
          form.resetFields();
        }}
        styles={{
          body: {
            padding: 0,
            background: "#89339c88",
            borderRadius: "20px",
          },
        }}
        title={
          <div
            style={{
              background: "#89339c",
              margin: "-20px -24px 0",
              padding: "18px 24px",
              borderRadius: "8px 8px 0 0",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div
                  style={{
                    width: 34,
                    height: 34,
                    background: "rgba(255,255,255,0.18)",
                    borderRadius: 8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <EditOutlined
                    style={{ color: "#fff", fontSize: 18 }}
                  />
                </div>

                <div>
                  <div
                    style={{
                      color: "#fff",
                      fontSize: 16,
                      fontWeight: 500,
                    }}
                  >
                    Edit Enquiry
                  </div>

                  <div
                    style={{
                      color: "rgba(255,255,255,0.7)",
                      fontSize: 14,
                      marginTop: 2,
                    }}
                  >
                    {editRecord?.id}
                  </div>
                </div>
              </div>
            </div>
          </div>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleEditSave}
          style={{ padding: "14px 22px 0" }}
        >
          <div
            style={{
              background: "#fff",
              border: "0.5px solid #e8e8e8",
              borderRadius: 12,
              padding: "14px 16px",
              marginBottom: 10,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <div style={{ width: 3, height: 16, background: "#89339c" }} />
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 500,
                  letterSpacing: "0.07em",
                  textTransform: "uppercase",
                  color: "#89339c",
                }}
              >
                Customer Information
              </span>
            </div>

            <Row gutter={[14, 10]}>
              <Col span={12}>
                <Form.Item
                  label="Customer Name"
                  name="name"
                  rules={[{ required: true }]}
                  style={{ marginBottom: 0 }}
                >
                  <Input style={{ height: 36 }} />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Mobile Number"
                  name="mobileno"
                  rules={[{ required: true }]}
                  style={{ marginBottom: 0 }}
                >
                  <Input style={{ height: 36 }} />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item name="enquiryby" label="Enquiry Update By">
                  <Input style={{ height: 36 }} />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item name="city" label="City" style={{ marginBottom: 0 }}>
                  <Input style={{ height: 36 }} />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Trip Details */}
          <div
            style={{
              background: "#fff",
              border: "0.5px solid #e8e8e8",
              borderRadius: 12,
              padding: "14px 16px",
              marginBottom: 10,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <div style={{ width: 3, height: 16, background: "#89339c" }} />
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 500,
                  letterSpacing: "0.07em",
                  textTransform: "uppercase",
                  color: "#89339c",
                }}
              >
                Trip Details
              </span>
            </div>

            <Row gutter={[14, 10]}>
              <Col span={12}>
                <Form.Item name="bookeddate" label="Date" style={{ marginBottom: 0 }}>
                  <DatePicker style={{ width: "100%", height: 36 }} />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item name="distance" label="Distance" style={{ marginBottom: 0 }}>
                  <Input style={{ height: 36 }} />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="From Location"
                  name="fromloc"
                  rules={[{ required: true }]}
                  style={{ marginBottom: 0 }}
                >
                  <Select showSearch placeholder="From Location" optionFilterProp="children">
                    {locations?.map((loc: any) => (
                      <Select.Option key={loc.ID} value={loc.PlaceName}>{loc.PlaceName}</Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="To Location"
                  name="toloc"
                  rules={[{ required: true }]}
                  style={{ marginBottom: 0 }}
                >
                  <Select showSearch placeholder="To Location" optionFilterProp="children">
                    {locations?.map((loc: any) => (
                      <Select.Option key={loc.ID} value={loc.PlaceName}>{loc.PlaceName}</Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Fare Type"
                  name="triptype"
                  style={{ marginBottom: 0 }}
                >
                  {/* <Select placeholder="Select Fare Type" onChange={(val) => {
                    form.setFieldsValue({ faredetails: undefined });
                    const vId = form.getFieldValue("vehicletype");
                    if (val === 2) dispatch(getPackagesRequest(vId, branchId));
                    if (val === 3) dispatch(getIntercityListRequest(vId, branchId));
                    if (val === 4) dispatch(getNightFareRequest(vId, branchId));
                  }}>
                    {tripTypes?.map((t: any) => (
                      <Select.Option key={t.id} value={Number(t.id)}>{t.name}</Select.Option>
                    ))}
                  </Select> */}
                  <Select
                    placeholder="Select Fare Type"
                    onChange={handleFareTypeChange}
                  >
                    {tripTypes?.map((t: any) => <Select.Option key={t.id} value={String(t.id)}>{t.name}</Select.Option>)}
                  </Select>
                </Form.Item>
              </Col>

              {/* {showFareDetails && (
                <Col span={12}>
                  <Form.Item label="Fare Detail" name="faredetails" style={{ marginBottom: 0 }}>
                    <Select
                      placeholder="Select Details"
                      onChange={(value) => {
                        const fareType = Number(form.getFieldValue("triptype"));
                        let selectedItem = null;
                        if (fareType === 2) {
                          selectedItem = packages?.find((pkg: any) => pkg.package === value);
                        } else if (fareType === 3) {
                          selectedItem = intercityList?.find((ic: any) => ic.tollerenckm === value);
                        } else if (fareType === 4) {
                          selectedItem = nightFare?.find((nf: any) => nf.package === value);
                        }
                        setSelectedFareDetail(selectedItem);
                      }}
                    >
                      {Number(form.getFieldValue("triptype")) === 3 ? (
                        intercityList?.map((ic: any) => (
                          <Select.Option key={ic.tollerenckm} value={ic.tollerenckm}>
                            {`${ic.basekm}-${ic.basefare} - ${convertMinutesToHrs(ic.baseminute)}`}
                          </Select.Option>
                        ))
                      ) : (
                        (Number(form.getFieldValue("triptype")) === 2 ? packages : nightFare)?.map((pkg: any) => (
                          <Select.Option key={pkg.id} value={pkg.package}>{pkg.package}</Select.Option>
                        ))
                      )}
                    </Select>
                  </Form.Item>
                </Col>
              )} */}

              <Col span={12}>
                <Form.Item
                  label="Goods"
                  name="goods"
                  style={{ marginBottom: 0 }}
                >
                  <Select showSearch placeholder="Select Goods Type" optionFilterProp="children">
                    {goodsTypes?.map((g: any) => (
                      <Select.Option key={g.goodsid} value={g.goodsname}>{g.goodsname}</Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Vehicle Type"
                  name="vehicletype"
                  style={{ marginBottom: 0 }}
                >
                  <Select showSearch placeholder="Select Vehicle" optionFilterProp="children">
                    {vehicleTypes?.map((v: any) => (
                      <Select.Option key={v.id} value={v.id}>{v.vehicletype}</Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Additional Details */}
          <div
            style={{
              background: "#fff",
              border: "0.5px solid #e8e8e8",
              borderRadius: 12,
              padding: "14px 16px",
              marginBottom: 10,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <div style={{ width: 3, height: 16, background: "#89339c" }} />
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 500,
                  letterSpacing: "0.07em",
                  textTransform: "uppercase",
                  color: "#89339c",
                }}
              >
                Additional Details
              </span>
            </div>

            <Row gutter={[14, 10]}>

              <Col span={12}>
                <Form.Item
                  label="Reason"
                  name="Reason"
                  style={{ marginBottom: 0 }}
                >
                  <Select placeholder="Select a reason">
                    <Select.Option value="Booked">Booked</Select.Option>
                    <Select.Option value="Confirm & call">Confirm & call</Select.Option>
                    <Select.Option value="Just Enquiry">Just Enquiry</Select.Option>
                    <Select.Option value="No Response">No Response</Select.Option>
                    <Select.Option value="Customer took another cab">Customer took another cab</Select.Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Remarks"
                  name="remarks"
                  style={{ marginBottom: 0 }}
                >
                  <Input.TextArea />
                </Form.Item>
              </Col>

              {/* <Col span={24}>
                <Form.Item
                  name="iscredit"
                  valuePropName="checked"
                  style={{ marginTop: 10, marginBottom: 0 }}
                >
                  <Checkbox>Credit Customer</Checkbox>
                </Form.Item>
              </Col> */}
            </Row>
          </div>

          {/* Footer */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 10,
              padding: "14px 0 16px",
              borderTop: "0.5px solid #f0f0f0",
              marginTop: 4,
            }}
          >
            <Button
              type="primary"
              htmlType="submit"
              loading={updateEnquiryLoading}
              style={{
                height: 36,
                minWidth: 140,
                borderRadius: 8,
                fontWeight: 500,
                border: "none",
                background:
                  "#89339c",
              }}
            >
              Update Enquiry
            </Button>

            <Button
              onClick={() => {
                setEditOpen(false);
                form.resetFields();
              }}
              style={{
                height: 36,
                borderRadius: 8,
                backgroundColor: "#f02929ce",
                color: "white",
              }}
            >
              Cancel
            </Button>
          </div>
        </Form>
      </Modal>

    </div>


  );
};

export default Enquiry;
